import React, { useEffect, useState } from 'react';
import { Plus, LayoutGrid, List } from 'lucide-react';
import { useProjectStore } from '../store/projectStore';
import { ProjectCard } from '../components/Projects/ProjectCard';
import { ProjectForm } from '../components/Projects/ProjectForm';
import { Modal } from '../components/common/Modal';
import { Button } from '../components/common/Button';
import { Header } from '../components/Layout/Header';
import { Project } from '../types';
import toast from 'react-hot-toast';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useSearchParams } from 'react-router-dom';

export function ProjectsPage() {
  const { projects, loading, fetchProjects, createProject, updateProject, deleteProject } = useProjectStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const activeWorkspaceId = useWorkspaceStore(state => state.activeWorkspaceId);
  const activeWorkspace = useWorkspaceStore(state => state.workspaces.find(workspace => workspace.id === state.activeWorkspaceId));
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchProjects();
  }, [activeWorkspaceId, fetchProjects]);

  useEffect(() => {
    if (searchParams.get('create') === '1') {
      setIsModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleCreate = async (data: Partial<Project>, memberIds?: string[]) => {
    try {
      await createProject(data, memberIds);
      setIsModalOpen(false);
      toast.success('Projeto criado!');
    } catch {
      toast.error('Falha ao criar projeto');
    }
  };

  const handleUpdate = async (data: Partial<Project>, addedMemberIds?: string[], removedMemberIds?: string[]) => {
    if (!editingProject) return;
    try {
      await updateProject(editingProject.id, data, addedMemberIds, removedMemberIds);
      setEditingProject(null);
      toast.success('Projeto atualizado!');
    } catch {
      toast.error('Falha ao atualizar projeto');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir este projeto? Todas as tarefas deste projeto também serão excluídas. Esta ação não pode ser desfeita.')) return;
    try {
      await deleteProject(id);
      toast.success('Projeto excluído');
    } catch {
      toast.error('Falha ao excluir projeto');
    }
  };

  return (
    <div>
      <Header title="Projetos" />
      <div className="p-6">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{projects.length} projetos</span>
            {activeWorkspace && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs text-gray-600 ring-1 ring-gray-200">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: activeWorkspace.color }} />
                {activeWorkspace.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setView('grid')}
                className={`p-2 ${view === 'grid' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <LayoutGrid size={16} />
              </button>
              <button
                onClick={() => setView('list')}
                className={`p-2 ${view === 'list' ? 'bg-primary-50 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <List size={16} />
              </button>
            </div>
            <Button icon={<Plus size={16} />} onClick={() => setIsModalOpen(true)}>
              Novo Projeto
            </Button>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && projects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus size={24} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum projeto ainda</h3>
            <p className="text-gray-500 mb-4">Crie o primeiro projeto desta área de trabalho</p>
            <Button icon={<Plus size={16} />} onClick={() => setIsModalOpen(true)}>
              Criar Projeto
            </Button>
          </div>
        )}

        {/* Projects Grid */}
        {!loading && projects.length > 0 && (
          <div className={view === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
            {projects.map(project => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={handleDelete}
                onEdit={setEditingProject}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Novo Projeto" size="lg">
        <ProjectForm onSubmit={handleCreate} onCancel={() => setIsModalOpen(false)} />
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={!!editingProject} onClose={() => setEditingProject(null)} title="Editar Projeto" size="lg">
        <ProjectForm
          initialData={editingProject || undefined}
          onSubmit={handleUpdate}
          onCancel={() => setEditingProject(null)}
        />
      </Modal>
    </div>
  );
}
